import Link from 'router/Link';
import { colorWithOpacity } from 'utils/helpers';
import { BASE_MARGIN, BREAKPOINTS } from 'utils/constants';
import { MENU_ICON_SIZE, VERTICAL_MENU_MAX_WIDTH } from './navigationUtils';
import createComponent from 'styles/fela/createComponent';
import { reserveBoldTextWidth, withBasePadding, widthRange, minWidth } from 'styles/fela/mixins';

export const Nav = createComponent(({ theme, isExpanded, position }) => ({
  background: theme.header.backgroundColor,
  color: theme.header.textColor,
  boxShadow: `0 0 10px 0 ${theme.color.overlayDarkExtraFaint}`,
  clipPath: 'inset(0px 0px -10px 0px)',
  flexGrow: 1,
  flexShrink: 1,
  left: 0,
  position: 'absolute',
  top: theme.header.height,
  width: '100%',

  extend: [
    {
      condition: position === 'horizontal',
      style: {
        background: 'none',
        color: 'inherit',
        boxShadow: 'none',
        height: theme.header.height,
        paddingBottom: 0,
        paddingTop: 0,
        position: 'static',
        display: 'flex',
      },
    },
    {
      condition: position === 'vertical',
      style: {
        maxWidth: `${VERTICAL_MENU_MAX_WIDTH}px`,
        left: 'auto',
        right: 0,
        height: '100vh',
        top: 0,
        zIndex: theme.zIndex.navigationMenu,
        transition: 'transform 150ms ease-out, opacity 200ms ease-in',
      },
    },
    {
      condition: position === 'vertical' && isExpanded,
      style: {
        transform: 'translateX(0)',
        opacity: 1,
      },
    },
    {
      condition: position === 'vertical' && !isExpanded,
      style: {
        transform: 'translateX(100%)',
        opacity: 0,
      },
    },
  ],
}), 'nav', ['isExpanded', 'position']);

export const MenuContainer = createComponent(({
  theme,
  position = 'vertical',
}) => ({
  display: 'block',
  width: '100%',
  fontSize: theme.fontSize.small,
  extend: [
    {
      condition: position === 'horizontal',
      style: {
        display: 'flex',
        flexShrink: '3.2', // resolved IE11 issue with blocking shrinking of empty container
        justifyContent: 'flex-start',
        '> a': {
          display: 'inline-block',
          width: 'auto',
        },
      },
    },
    {
      condition: position === 'vertical',
      style: {
        paddingTop: theme.header.height,
        justifyContent: 'flex-start',
        paddingBottom: theme.header.height,
        height: '100%',
        overflowX: 'hidden',
        overflowY: 'auto',
        '::-webkit-scrollbar': {
          width: '6px',
        },
        '::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '::-webkit-scrollbar-thumb': {
          backgroundColor: colorWithOpacity(theme.header.textColor, 20),
          borderRadius: '3px',
          ':hover': {
            backgroundColor: colorWithOpacity(theme.header.textColor, 30),
          },
        },
      },
    },
  ],
}), 'div', ['position']);

export const MenuWrapper = createComponent(({ isHidden }) => ({
  display: 'flex',
  flexGrow: 1,
  extend: [
    {
      condition: isHidden,
      style: {
        visibility: 'hidden',
      },
    },
  ],
}), 'div', ['isHidden']);

export const LogoLink = createComponent(({ theme }) => ({
  marginRight: theme.margin.small,
  display: 'flex',
  height: theme.header.logo.height,
  width: theme.header.logo.width,
  alignItems: 'center',
  flexShrink: 0,
}), Link);

export const LogoImage = createComponent(() => ({
  height: 'auto',
  width: 'auto',
  maxHeight: '100%',
  maxWidth: '100%',
}), 'img');


export const PersistentNavigationContainer = createComponent(({ theme, offCanvas }) => ({
  display: 'flex',
  alignItems: 'center',
  marginLeft: 'auto',
  flexShrink: 0,
  fontSize: theme.fontSize.small,
  extend: [
    {
      condition: offCanvas,
      style: {
        position: 'absolute',
        top: -10000,
        left: -10000,
      },
    },
  ],
}), 'div', ['offCanvas']);

export const StyledPersistentNavigation = createComponent(() => ({
  display: 'flex',
  alignItems: 'center',
}), 'nav');

export const Hamburger = createComponent(({ visible, theme }) => ({
  alignSelf: 'center',
  background: 'transparent',
  border: 0,
  color: 'inherit',
  cursor: 'pointer',
  display: 'block',
  minHeight: theme.header.height,
  minWidth: MENU_ICON_SIZE,
  marginLeft: theme.margin.large,
  outline: 0,
  textAlign: 'right',
  '& svg': {
    verticalAlign: 'middle',
  },
  extend: [
    {
      condition: !visible,
      style: {
        display: 'none',
      },
    },
  ],
}), 'button', ['visible']);

export const HeaderContainer = createComponent(({ theme, opacity, scroll }) => ({
  color: theme.header.textColor,
  height: theme.header.height,
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: theme.zIndex.header,
  extend: [
    withBasePadding(),
  ],
  '& a': {
    outline: 'none',
    '-webkit-tap-highlight-color': 'transparent',
  },
  '& button': {
    outline: 'none',
    '-webkit-tap-highlight-color': 'transparent',
  },
  '& a:focus': {
    outline: 'none',
    boxShadow: 'none',
  },
  '& a:active': {
    outline: 'none',
    boxShadow: 'none',
  },
  '& button:focus': {
    outline: 'none',
    boxShadow: 'none',
  },
  '& button:active': {
    outline: 'none',
    boxShadow: 'none',
  },
  ':before': {
    // header gradient background before scroll
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    opacity: scroll === 'true' ? 0 : 1,
    background: `linear-gradient(${colorWithOpacity(theme.header.backgroundColor, opacity.beforeScroll.gradientStart)}, ${colorWithOpacity(theme.header.backgroundColor, opacity.beforeScroll.gradientEnd)})`,
    extend: [
      {
        // apply transition effect if before or after scroll header background has transparency
        condition: opacity.beforeScroll.gradientStart < 100
          || opacity.beforeScroll.gradientEnd < 100
          || opacity.afterScroll < 100,
        style: {
          extend: [
            {
              // do not apply transition effect if header has no gradient effect before scroll
              // and header same color before and after scroll
              condition: !(opacity.beforeScroll.gradientStart === opacity.beforeScroll.gradientEnd
                && opacity.beforeScroll.gradientEnd === opacity.afterScroll),
              style: {
                transition: `opacity 200ms ${scroll === 'true' ? 'ease-in' : 'ease-out'}`,
              },
            },
          ],
        },
      },
    ],
  },
  ':after': {
    // header gradient background after scroll
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    opacity: scroll === 'true' ? 1 : 0,
    background: `linear-gradient(${colorWithOpacity(theme.header.backgroundColor, opacity.afterScroll)}, ${colorWithOpacity(theme.header.backgroundColor, opacity.afterScroll)})`,
    extend: [
      {
        // apply transition effect if before or after scroll header background has transparency
        condition: opacity.beforeScroll.gradientStart < 100
          || opacity.beforeScroll.gradientEnd < 100
          || opacity.afterScroll < 100,
        style: {
          extend: [
            {
              // do not apply transition effect if header has no gradient effect before scroll
              // and header same color before and after scroll
              condition: !(opacity.beforeScroll.gradientStart === opacity.beforeScroll.gradientEnd
                && opacity.beforeScroll.gradientEnd === opacity.afterScroll),
              style: {
                transition: `opacity 200ms ${scroll === 'true' ? 'ease-out' : 'ease-in'}`,
              },
            },
          ],
        },
      },
    ],
  },
}), 'header', ['opacity']);

export const Content = createComponent(() => ({
  position: 'absolute',
  zIndex: 1,
  left: 0,
  top: 0,
  right: 0,
  bottom: 0,
  padding: 'inherit',
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'flex-start',
  height: '100%',
}));

export const MenuLink = createComponent(({ theme, active, position }) => ({
  cursor: 'pointer',
  color: 'inherit',
  display: 'block',
  lineHeight: theme.header.height,
  marginLeft: theme.margin.large,
  textAlign: 'center',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  ':hover': {
    fontWeight: 'bold',
  },
  extend: [
    reserveBoldTextWidth('data-content'),
    {
      condition: active,
      style: {
        fontWeight: 'bold',
      },
    },
    {
      condition: position === 'vertical',
      style: {
        lineHeight: 'normal',
        marginBottom: theme.margin.large,
        textAlign: 'left',
        fontSize: theme.fontSize.normal,
        display: 'block',
        width: 'fit-content',
        // Fix layout shift: prevent bold text on hover from pushing menu items below downward
        minHeight: theme.fontSize.medium,
      },
    },
  ],
}), Link, ['active', 'position']);
MenuLink.displayName = 'MenuLink';

export const HeaderSpacer = createComponent(({ theme }) => ({
  width: '100%',
  height: theme.header.height,
  background: theme.color.page,
}));

export const CloseButton = createComponent(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: BASE_MARGIN.small,
  background: 'transparent',
  border: 0,
  color: 'inherit',
  cursor: 'pointer',
  display: 'block',
  minHeight: theme.header.height,
  minWidth: MENU_ICON_SIZE,
  outline: 0,
  padding: 0,
  margin: 0,
  textAlign: 'right',
  alignSelf: 'center',
  zIndex: 1,
  '& svg': {
    verticalAlign: 'middle',
  },
  extend: [
    widthRange(BREAKPOINTS.sm, BREAKPOINTS.md, {
      right: BASE_MARGIN.medium,
    }),
    minWidth(BREAKPOINTS.md, {
      right: BASE_MARGIN.medium,
    }),
  ],
}), 'button');

export const MenuNavigationWrapper = createComponent(({ offCanvas }) => ({
  extend: [
    {
      condition: offCanvas,
      style: {
        position: 'absolute',
        top: -10000,
        left: -10000,
      },
    },
  ],
}), 'div', ['offCanvas']);

export const SignUpButton = createComponent(({ theme, position }) => ({
  cursor: 'pointer',
  color: 'inherit',
  display: 'inline-block',
  textAlign: 'center',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  border: '2px solid currentColor',
  borderRadius: theme.radius.default,
  padding: `${theme.margin.fine} ${theme.margin.medium}`,
  marginLeft: theme.margin.large,
  transition: 'background-color 200ms ease-out',
  ':hover': {
    backgroundColor: colorWithOpacity(theme.header.textColor, 20),
  },
  extend: [
    {
      condition: position === 'vertical',
      style: {
        lineHeight: 'normal',
        marginBottom: theme.margin.large,
        fontSize: theme.fontSize.normal,
        textAlign: 'center',
        display: 'block',
        width: 'fit-content',
      },
    },
  ],
}), Link, ['active', 'position']);
SignUpButton.displayName = 'SignUpButton';

export const NavigationOverlay = createComponent(({ theme, isVisible }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: theme.color.overlayModal,
  zIndex: theme.zIndex.navigationOverlay,
  transition: 'opacity 200ms ease-out',
  extend: [
    {
      condition: isVisible,
      style: {
        opacity: 1,
        pointerEvents: 'auto',
      },
    },
    {
      condition: !isVisible,
      style: {
        opacity: 0,
        pointerEvents: 'none',
      },
    },
  ],
}), 'div', ['isVisible']);

export const StyledSearchLink = createComponent(({ theme }) => ({
  display: 'flex !important',
  flexDirection: 'column',
  alignItems: 'flex-end',
  justifyContent: 'center',
  minHeight: theme.header.height,
  width: MENU_ICON_SIZE,
}), MenuLink, []);

export const StyledSettingsLink = createComponent(({ theme, active }) => ({
  width: MENU_ICON_SIZE,
  minHeight: theme.header.height,
  '& svg': {
    verticalAlign: 'middle',
  },
  ':hover': {
    fontWeight: 'normal',
  },
  extend: [
    {
      condition: active,
      style: {
        fontWeight: 'normal',
      },
    },
  ],
}), MenuLink, ['active']);
